/**
 * Network Authentication Wrapper
 * Staffing Command Center
 * Built and maintained by Angel Carreon (acarreom)
 * Copyright © 2025. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { validateDevCredentials, getDevCredentials } from '../utils/security';

const NetworkAuthWrapper = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detectedNetwork, setDetectedNetwork] = useState('');
  const [networkInfo, setNetworkInfo] = useState({});
  const [devEmail, setDevEmail] = useState('');
  const [devPassword, setDevPassword] = useState('');
  const [devError, setDevError] = useState('');
  const [isDevMode, setIsDevMode] = useState(false);
  const [isDevModeOpen, setIsDevModeOpen] = useState(false);

  // Allowed network names
  const allowedNetworks = [
    'asin282',
    'asin324', 
    'asin282r',
    'amazon_internal' // For users with hostname pattern like "72-21-198-67.amazon.com"
  ];

  useEffect(() => {
    // Check network-based authentication
    checkNetworkAuthentication();
  }, []);

  const checkNetworkAuthentication = async () => {
    console.log('=== Starting Network Authentication Check ===');
    
    try {
      // First check if user is already authenticated (stored in localStorage)
      const storedAuth = localStorage.getItem('network_auth_approved');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        const authAge = Date.now() - authData.timestamp;
        const maxAge = 8 * 60 * 60 * 1000; // 8 hours
        
        if (authAge < maxAge) {
          // Validate stored Dev Mode sessions strictly against approved credentials
          // Check for both new format (devMode: true) and legacy format (network: 'dev_mode')
          // Also check if email matches dev credentials as additional fallback
          const devCreds = getDevCredentials();
          if (authData.devMode === true || authData.network === 'dev_mode' || 
              (authData.email === devCreds.email && authData.approved === true)) {
            if (authData.email === devCreds.email) {
              console.log('Found valid stored Dev Mode authentication (legacy or new format)');
              
              // Upgrade legacy session to new format if needed
              if (!authData.devMode) {
                console.log('Upgrading legacy dev mode session to new format');
                const upgradedAuthData = {
                  ...authData,
                  devMode: true,
                  network: 'dev_mode'
                };
                localStorage.setItem('network_auth_approved', JSON.stringify(upgradedAuthData));
              }
              
              setDetectedNetwork('dev_mode');
              setIsDevMode(true);
              setIsAuthenticated(true);
              setLoading(false);
              return;
            } else {
              // Invalid dev session - clear it
              console.log('Invalid dev session found, clearing...');
              localStorage.removeItem('network_auth_approved');
            }
          } else {
            console.log('Found valid stored authentication for network:', authData.network);
            setDetectedNetwork(authData.network);
            setNetworkInfo(authData.networkInfo || {});
            setIsAuthenticated(true);
            setLoading(false);
            return;
          }
        } else {
          // Remove expired authentication
          localStorage.removeItem('network_auth_approved');
        }
      }

      // Get user's network information
      const networkData = await getUserNetworkInfo();
      console.log('Detected network info:', networkData);
      setDetectedNetwork(networkData.networkName);
      setNetworkInfo(networkData);

      // SECURITY: Only allow EXACT matches of authorized network names
      const isAllowedNetwork = allowedNetworks.some(allowedNet => 
        networkData.networkName.toLowerCase() === allowedNet.toLowerCase()
      );

      console.log('=== SECURITY CHECK ===');
      console.log('Network allowed:', isAllowedNetwork);
      console.log('Detected network:', networkData.networkName);
      console.log('Allowed networks:', allowedNetworks);
      console.log('Exact match required for access');

      // SECURITY: Default to DENY access unless exact match found
      if (isAllowedNetwork && networkData.networkName !== 'Unknown' && networkData.networkName !== '') {
        console.log('✅ SECURITY: Access granted - exact network match found');
        // Store authentication
        const authData = {
          network: networkData.networkName,
          networkInfo: networkData,
          timestamp: Date.now(),
          approved: true
        };
        localStorage.setItem('network_auth_approved', JSON.stringify(authData));
        
        setIsAuthenticated(true);
        setError('');
      } else {
        console.log('❌ SECURITY: Access denied - no exact network match');
        console.log('DEBUGGING INFO:');
        console.log('- Network name detected:', networkData.networkName);
        console.log('- ASN:', networkData.asn);
        console.log('- ISP:', networkData.isp);
        console.log('- Hostname:', networkData.hostname);
        console.log('- Client IP:', networkData.clientIP);
        console.log('- Country:', networkData.country);
        console.log('- City:', networkData.city);
        console.log('- Local IPs:', networkData.localIPs);
        
        setIsAuthenticated(false);
        
        // Provide professional error message
        if (networkData.networkName === 'Unknown') {
          setError(`Unable to automatically detect your network connection. Please contact your administrator for assistance.`);
        } else {
          setError(`Access denied. Your network connection is not authorized for this application.`);
        }
      }
    } catch (error) {
      console.error('Network authentication error:', error);
      setIsAuthenticated(false);
      setError('Unable to verify network authentication. Please contact your administrator.');
    }

    setLoading(false);
  };

  const getUserNetworkInfo = async () => {
    console.log('Attempting to detect network information...');
    
    const networkData = {
      networkName: 'Unknown',
      ssid: 'Unknown',
      hostname: 'Unknown',
      clientIP: 'Unknown',
      method: 'unknown'
    };

    try {
      // Method 1: Try to get client IP and ISP information
      const ipInfo = await getClientIP();
      networkData.clientIP = ipInfo.ip || 'Unknown';
      networkData.isp = ipInfo.isp || 'Unknown';
      networkData.asn = ipInfo.asn || 'Unknown';
      networkData.country = ipInfo.country || 'Unknown';
      networkData.city = ipInfo.city || 'Unknown';
      
      // Method 2: Try to get hostname information
      const hostnameInfo = await getHostnameInfo();
      networkData.hostname = hostnameInfo;
      
      // Method 3: Try WebRTC-based network detection for local IPs
      const webrtcInfo = await getWebRTCNetworkInfo();
      if (webrtcInfo.localIPs && webrtcInfo.localIPs.length > 0) {
        networkData.localIPs = webrtcInfo.localIPs;
      }
      
      // Method 4: Try to infer network name from various sources (including ISP)
      const inferredNetwork = await inferNetworkName(networkData);
      networkData.networkName = inferredNetwork || 'Unknown';
      
      // Method 5: Check for network-specific indicators
      const networkIndicators = await checkNetworkIndicators();
      if (networkIndicators.detected) {
        networkData.networkName = networkIndicators.name;
        networkData.method = 'indicators';
      }

      console.log('Network detection complete:', networkData);
      return networkData;
      
    } catch (error) {
      console.error('Network detection failed:', error);
      return networkData;
    }
  };

  const getClientIP = async () => {
    try {
      // Try multiple IP detection services that provide ISP information
      const ipServices = [
        {
          url: 'https://ipapi.co/json/',
          parser: (data) => ({
            ip: data.ip,
            isp: data.org || data.as_name || data.company_name,
            asn: data.asn,
            country: data.country_name,
            city: data.city
          })
        },
        {
          url: 'https://api.ipify.org?format=json',
          parser: (data) => ({
            ip: data.ip,
            isp: null
          })
        },
        {
          url: 'https://httpbin.org/ip',
          parser: (data) => ({
            ip: data.origin,
            isp: null
          })
        }
      ];

      for (const service of ipServices) {
        try {
          const response = await fetch(service.url);
          const data = await response.json();
          const parsed = service.parser(data);
          
          if (parsed.ip) {
            console.log(`Client IP detected via ${service.url}:`, parsed.ip);
            if (parsed.isp) {
              console.log(`ISP detected:`, parsed.isp);
            }
            return parsed;
          }
        } catch (error) {
          console.log(`IP service ${service.url} failed:`, error);
        }
      }
    } catch (error) {
      console.error('All IP detection methods failed:', error);
    }
    
    return { ip: null, isp: null };
  };

  const getHostnameInfo = async () => {
    try {
      // Get hostname from current location
      const hostname = window.location.hostname;
      console.log('Current hostname:', hostname);
      
      // Try to extract network information from hostname
      if (hostname && hostname !== 'localhost' && !hostname.includes('vercel.app')) {
        return hostname;
      }
      
      return 'Unknown';
    } catch (error) {
      console.error('Hostname detection failed:', error);
      return 'Unknown';
    }
  };

  const inferNetworkName = async (networkData) => {
    console.log('Inferring network name from available data...');
    
    try {
      // Method 1: Check for Amazon ASN (MOST RELIABLE - AS16509)
      if (networkData.asn && networkData.asn !== 'Unknown') {
        console.log('Checking ASN:', networkData.asn);
        
        // Check for Amazon's Autonomous System Number
        if (networkData.asn === 'AS16509' || networkData.asn === '16509') {
          console.log('Amazon ASN (AS16509) detected, granting access');
          return 'amazon_internal';
        }
      }
      
      // Method 2: Check for Amazon ISP patterns (fallback)
      if (networkData.isp && networkData.isp !== 'Unknown') {
        console.log('Checking ISP:', networkData.isp);
        
        // Check for Amazon ISP patterns (including VPN connections)
        const ispLower = networkData.isp.toLowerCase();
        if (ispLower.includes('amazon corporate') || 
            ispLower.includes('amazon.com') || 
            ispLower.includes('amazon technologies') ||
            ispLower.includes('amazon corporate llc') ||
            ispLower.includes('amazon technologies inc')) {
          console.log('Amazon ISP detected, granting access:', networkData.isp);
          return 'amazon_internal';
        }
      }
      
      // Method 3: Check for Amazon hostname pattern (e.g., "72-21-198-67.amazon.com")
      if (networkData.hostname && networkData.hostname !== 'Unknown') {
        console.log('Checking hostname pattern:', networkData.hostname);
        
        // Check for Amazon internal network pattern
        if (networkData.hostname.toLowerCase().endsWith('.amazon.com')) {
          console.log('Amazon hostname pattern detected, granting access');
          return 'amazon_internal';
        }
        
        // Also check for exact matches or specific patterns for the original networks
        for (const allowedNet of allowedNetworks) {
          // Only allow exact matches or very specific patterns
          if (networkData.hostname.toLowerCase() === allowedNet.toLowerCase() ||
              networkData.hostname.toLowerCase() === `${allowedNet.toLowerCase()}.local` ||
              networkData.hostname.toLowerCase().startsWith(`${allowedNet.toLowerCase()}-`)) {
            console.log(`Network name inferred from hostname: ${allowedNet}`);
            return allowedNet;
          }
        }
      }
      
      // Method 4: Log additional info for debugging
      console.log('Network inference details:');
      console.log('- ASN:', networkData.asn);
      console.log('- ISP:', networkData.isp);
      console.log('- Hostname:', networkData.hostname);
      console.log('- Client IP:', networkData.clientIP);
      console.log('- Country:', networkData.country);
      console.log('- City:', networkData.city);
      
      return null;
    } catch (error) {
      console.error('Network name inference failed:', error);
      return null;
    }
  };

  const checkNetworkIndicators = async () => {
    console.log('Network indicator checking disabled for security');
    
    // SECURITY: This function has been disabled because it was too permissive
    // and could grant access to unauthorized users through substring matching
    // in user agents, cookies, or other browser data.
    
    return { detected: false, name: null };
  };

  const getWebRTCNetworkInfo = async () => {
    return new Promise((resolve) => {
      const networkInfo = {
        localIPs: [],
        networkType: 'unknown'
      };
      
      try {
        const rtc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });

        rtc.createDataChannel('');
        
        rtc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidate = event.candidate.candidate;
            // Extract IP addresses from ICE candidates
            const ipRegex = /(\d{1,3}\.){3}\d{1,3}/g;
            const ips = candidate.match(ipRegex);
            
            if (ips) {
              ips.forEach(ip => {
                // Collect all local IPs for network analysis
                if (!networkInfo.localIPs.includes(ip)) {
                  networkInfo.localIPs.push(ip);
                }
                
                // Determine network type based on IP ranges
                if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.')) {
                  networkInfo.networkType = 'private';
                }
              });
            }
          }
        };

        rtc.createOffer()
          .then(offer => rtc.setLocalDescription(offer))
          .catch(() => {});

        // Timeout after 3 seconds
        setTimeout(() => {
          rtc.close();
          resolve(networkInfo);
        }, 3000);
        
      } catch (error) {
        console.error('WebRTC network detection failed:', error);
        resolve(networkInfo);
      }
    });
  };





  const handleLogout = () => {
    localStorage.removeItem('network_auth_approved');
    setIsAuthenticated(false);
    setDetectedNetwork('');
    setNetworkInfo({});
  };

  const handleRetryAuth = () => {
    setLoading(true);
    setError('');
    checkNetworkAuthentication();
  };

  const handleDevModeLogin = (e) => {
    e.preventDefault();
    setDevError('');
    const email = devEmail.trim();
    const password = devPassword.trim();
    if (!email || !password) {
      setDevError('Please enter both email and password');
      return;
    }
    if (!validateDevCredentials(email, password)) {
      setDevError('Dev Mode access denied. Invalid credentials.');
      return;
    }
    // Store a tightly scoped dev_mode session
    const authData = {
      network: 'dev_mode',
      devMode: true,
      email,
      timestamp: Date.now(),
      approved: true
    };
    localStorage.setItem('network_auth_approved', JSON.stringify(authData));
    setIsDevMode(true);
    setIsAuthenticated(true);
    setError('');
  };

  const handleExitDevMode = () => {
    // Clear dev mode authentication
    localStorage.removeItem('network_auth_approved');
    setIsDevMode(false);
    setIsAuthenticated(false);
    setDetectedNetwork('');
    setNetworkInfo({});
    setDevEmail('');
    setDevPassword('');
    setDevError('');
    setError('');
    setLoading(true);
    
    // Re-check network authentication
    setTimeout(() => {
      checkNetworkAuthentication();
    }, 100);
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-4">Checking network authentication...</p>
          <p className="text-sm text-gray-500">Verifying your access permissions based on your network connection.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Staffing Board Access</h1>
            <p className="text-gray-600">Access restricted to authorized networks</p>
            {detectedNetwork && detectedNetwork !== 'Unknown' && (
              <p className="text-sm text-gray-500 mt-2">Detected Network: <strong>{detectedNetwork}</strong></p>
            )}
            {networkInfo.isp && networkInfo.isp !== 'Unknown' && (
              <p className="text-sm text-gray-500 mt-1">ISP: <strong>{networkInfo.isp}</strong></p>
            )}
            {networkInfo.asn && networkInfo.asn !== 'Unknown' && (
              <p className="text-sm text-gray-500 mt-1">ASN: <strong>{networkInfo.asn}</strong></p>
            )}
            {networkInfo.clientIP && networkInfo.clientIP !== 'Unknown' && (
              <p className="text-sm text-gray-500 mt-1">Client IP: <strong>{networkInfo.clientIP}</strong></p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}



          <div className="space-y-4">
            <button
              onClick={handleRetryAuth}
              className="w-full bg-green-500 text-white py-3 px-4 rounded-md hover:bg-green-600 transition-colors font-medium"
            >
              🔄 Retry Network Authentication
            </button>
            
            <div className="text-center">
              <p className="text-sm text-gray-500">
                Access is automatically granted based on your network connection.<br/>
                Please contact your administrator if you continue to have access issues.
              </p>
            </div>

            {/* Dev Mode - Approved credentials only */}
            <div className="mt-8 border-t border-gray-200 pt-6">
              <div className="text-center">
                <button
                  onClick={() => setIsDevModeOpen(!isDevModeOpen)}
                  className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  <span>🚧 Developer Access</span>
                  <svg 
                    className={`ml-2 h-4 w-4 transform transition-transform ${isDevModeOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <p className="text-xs text-gray-500 mt-2">Bypass network check with approved credentials only</p>
              </div>
              
              {isDevModeOpen && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  {devError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm">
                      {devError}
                    </div>
                  )}
                  <form onSubmit={handleDevModeLogin}>
                    <div className="space-y-3">
                      <input
                        type="email"
                        placeholder="Email"
                        value={devEmail}
                        onChange={(e) => setDevEmail(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={devPassword}
                        onChange={(e) => setDevPassword(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="submit"
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        title="Dev Mode access for approved credentials only"
                      >
                        🚧 Dev Mode Login
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Main app content */}
      {typeof children === 'function' 
        ? children({ 
            isDevMode, 
            onExitDevMode: handleExitDevMode,
            isAuthenticated
          })
        : React.cloneElement(children, { 
            isDevMode, 
            onExitDevMode: handleExitDevMode,
            isAuthenticated
          })
      }
    </div>
  );
};

export default NetworkAuthWrapper; 